import { useEffect, useState, useCallback, useRef } from "react"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import TextField from "@mui/material/TextField"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"
import DataTable from "monitor/Tables/DataTable"
import Icon from "@mui/material/Icon"
import Swal from "sweetalert2"
import { showToast } from "utils/swalToast"
import MDButton from "components/MDButton"
import {
  CircularProgress, 
  Tooltip,
  Backdrop,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material"
import MDPagination from "components/MDPagination"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/l10n/es.js"
import "flatpickr/dist/themes/dark.css"
import {
  fetchReports as fetchReportsService,
  deleteReport,
  downloadReport,
  getReportProgress,
  pauseReport,
  resumeReport,
  cancelReport,
} from "./service"
import { useMaterialUIController } from "context"
/* eslint-disable react/prop-types */

// Cache global: el DataTable remonta las filas en cada fetch y perdería el display animado.
const animatedPercentCache = new Map()
const POLLING_INTERVAL_MS = 10000
const FAST_POLLING_INTERVAL_MS = 2500
const ACTIVE_REPORT_STATUSES = new Set(["PENDING", "RUNNING", "PAUSED"])
const TERMINAL_REPORT_STATUSES = new Set([
  "DONE",
  "COMPLETED",
  "FINISHED",
  "SUCCESS",
  "ERROR",
  "FAILED",
  "CANCELED",
  "CANCELLED",
])
const ERROR_REPORT_STATUSES = new Set(["ERROR", "FAILED"])
const CANCELED_REPORT_STATUSES = new Set(["CANCELED", "CANCELLED"])
const REPORT_DESCRIPTION_STATUSES = new Map([
  ["PENDIENTE", "PENDING"],
  ["PENDING", "PENDING"],
  ["EN PROCESO", "RUNNING"],
  ["PROCESANDO", "RUNNING"],
  ["GENERANDO", "RUNNING"],
  ["RUNNING", "RUNNING"],
  ["PAUSADO", "PAUSED"],
  ["PAUSADA", "PAUSED"],
  ["PAUSED", "PAUSED"],
  ["ERROR", "ERROR"],
  ["FALLIDO", "ERROR"],
  ["FAILED", "ERROR"],
  ["CANCELADO", "CANCELED"],
  ["CANCELADA", "CANCELED"],
  ["CANCELED", "CANCELED"],
  ["CANCELLED", "CANCELED"],
])

const normalizeReportStatus = (status) => (status ? String(status).toUpperCase() : "")

const getStatusFromDescription = (description) =>
  REPORT_DESCRIPTION_STATUSES.get(normalizeReportStatus(description)) || ""

const getReportStatus = (report, progress) =>
  normalizeReportStatus(progress?.status ?? report?.status ?? report?.estado) ||
  getStatusFromDescription(report?.descripcion)

const isTerminalReportStatus = (status) =>
  TERMINAL_REPORT_STATUSES.has(normalizeReportStatus(status))

const isActiveReport = (report) => {
  const status = getReportStatus(report)
  if (status) return ACTIVE_REPORT_STATUSES.has(status)
  return !report?.descripcion
}

const AnimatedPercent = ({ reportId, value }) => {
  const target = value || 0
  const [display, setDisplay] = useState(() => {
    const cached = animatedPercentCache.get(reportId)
    if (cached !== undefined) return cached
    if (target > 0) {
      animatedPercentCache.set(reportId, target)
      return target
    }
    return 0
  })
  const displayRef = useRef(display)
  displayRef.current = display

  useEffect(() => {
    if (displayRef.current >= target) {
      if (displayRef.current !== target) {
        animatedPercentCache.set(reportId, target)
        setDisplay(target)
      }
      return undefined
    }
    const id = setInterval(() => {
      const current = displayRef.current
      if (current >= target) {
        clearInterval(id)
        return
      }
      const next = current + 1
      animatedPercentCache.set(reportId, next)
      setDisplay(next)
    }, 60)
    return () => clearInterval(id)
  }, [target, reportId])

  return `${display}%`
}

function ReportCenterPage() {
  const [data, setData] = useState([])
  const [userFilter, setUserFilter] = useState("")
  const [extensionFilter, setExtensionFilter] = useState("")
  const [serviceFilter, setServiceFilter] = useState("")
  const [fileNameFilter, setFileNameFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [fechaDesde, setFechaDesde] = useState()
  const [fechaHasta, setFechaHasta] = useState()
  const [loading, setLoading] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [progressData, setProgressData] = useState({})
  const reportsInProgressRef = useRef([])
  const completionRefreshRef = useRef({})
  const [controller] = useMaterialUIController()
  const { darkMode } = controller

  const columns = [
    {
      Header: "Fecha de Generación",
      accessor: "dateTime",
      width: "20%",
      Cell: ({ value }) => (
        <>
          <MDTypography component="p" variant="caption" color="text" fontWeight="regular">
            {formatIsoDate(value, "DATE")}
          </MDTypography>
          <MDTypography component="p" variant="caption" color="text" fontWeight="light">
            {formatIsoDate(value, "TIME")} hs.
          </MDTypography>
        </>
      ),
    },
    { Header: "Extensión", accessor: "documentType", width: "15%" },
    { Header: "Nombre de archivo", accessor: "name", width: "20%" },
    { Header: "Servicio", accessor: "servicio", width: "20%" },
    { Header: "Usuario", accessor: "usuario", width: "15%" },
    {
      Header: "Acción",
      accessor: "id",
      align: "center",
      width: "10%",
      Cell: ({ value: id, row }) => {
        const d = row.original
        const p = progressData[id] // { id, progress, status, ... } o undefined
        const progressPct = p?.progress ?? 0
        const status = getReportStatus(d, p)
        const isActiveStatus = ACTIVE_REPORT_STATUSES.has(status)
        const isPaused = status === "PAUSED"
        const isRunning = status === "RUNNING" || status === "PENDING"
        const isError = d.descripcion === "Error" || ERROR_REPORT_STATUSES.has(status)
        const isCanceled =
          d.descripcion === "Cancelado" || CANCELED_REPORT_STATUSES.has(status)
        const isTerminalWithoutFile = isTerminalReportStatus(status) && !d.descripcion

        if (isError) {
          return (
            <MDBox>
              <Tooltip title="Ocurrió un error al generar el reporte">
                <MDButton sx={{ mr: 1 }} variant="gradient" color="error" iconOnly>
                  <Icon>error</Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Eliminar reporte">
                <MDButton variant="gradient" color="error" iconOnly onClick={() => handleDelete(d.id)}>
                  <Icon>delete</Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          )
        }

        if (isCanceled) {
          return (
            <MDBox>
              <Tooltip title="Reporte cancelado">
                <MDButton sx={{ mr: 1 }} variant="gradient" color="secondary" iconOnly>
                  <Icon>cancel</Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Eliminar reporte">
                <MDButton variant="gradient" color="error" iconOnly onClick={() => handleDelete(d.id)}>
                  <Icon>delete</Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          )
        }

        if (d.descripcion && !isActiveStatus) {
          return (
            <MDBox>
              <Tooltip title="Descargar el reporte generado">
                <MDButton
                  sx={{ mr: 1 }}
                  variant="gradient"
                  color="success"
                  iconOnly
                  onClick={() => handleDownload(d.id, d.name)}
                >
                  <Icon>download</Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Eliminar reporte">
                <MDButton variant="gradient" color="error" iconOnly onClick={() => handleDelete(d.id)}>
                  <Icon>delete</Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          )
        }

        const isFinalizing = progressPct >= 100 || isTerminalWithoutFile

        return (
          <MDBox display="inline-flex" alignItems="center" gap={0.5}>
            <MDBox
              sx={{
                position: "relative",
                display: "inline-flex",
                mr: 1,
                verticalAlign: "middle",
              }}
            >
              <CircularProgress
                size={44}
                color={isPaused ? "info" : "warning"}
                variant={isPaused ? "determinate" : "indeterminate"}
                value={isPaused ? progressPct : undefined}
              />
              <MDBox
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MDTypography
                  variant="caption"
                  component="div"
                  color="text"
                  sx={{ fontSize: "10px" }}
                >
                  <AnimatedPercent reportId={d.id} value={progressPct} />
                </MDTypography>
              </MDBox>
            </MDBox>

            {isFinalizing ? (
              <MDTypography variant="caption" color="text" sx={{ ml: 0.5 }}>
                Finalizando...
              </MDTypography>
            ) : (
              <>
                {isRunning && (
                  <Tooltip title="Pausar descarga">
                    <MDButton
                      sx={{ mr: 0.5 }}
                      variant="gradient"
                      color="warning"
                      iconOnly
                      onClick={() => handlePause(d.id)}
                    >
                      <Icon>pause</Icon>
                    </MDButton>
                  </Tooltip>
                )}
                {isPaused && (
                  <Tooltip title="Reanudar descarga">
                    <MDButton
                      sx={{ mr: 0.5 }}
                      variant="gradient"
                      color="success"
                      iconOnly
                      onClick={() => handleResume(d.id)}
                    >
                      <Icon>play_arrow</Icon>
                    </MDButton>
                  </Tooltip>
                )}
                {(isRunning || isPaused) && (
                  <Tooltip title="Cancelar descarga">
                    <MDButton
                      variant="gradient"
                      color="error"
                      iconOnly
                      onClick={() => handleCancel(d.id)}
                    >
                      <Icon>close</Icon>
                    </MDButton>
                  </Tooltip>
                )}
              </>
            )}
          </MDBox>
        )
      },
    },
  ]

  const formatDateForApi = (date) => {
    if (!date) return ""
    const pad = (n) => (n < 10 ? "0" + n : n)
    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate()) +
      " " +
      pad(date.getHours()) +
      ":" +
      pad(date.getMinutes()) +
      ":" +
      pad(date.getSeconds())
    )
  }

  const buildFilters = () => ({
    extension: extensionFilter,
    fileName: fileNameFilter,
    servicio: serviceFilter,
    usuario: userFilter,
    status: statusFilter,
    fechaDesde: formatDateForApi(fechaDesde),
    fechaHasta: formatDateForApi(fechaHasta),
  })

  const formatIsoDate = (isoString, type = "DATE") => {
    const [datePart, timePart] = isoString.split("T")
    const [year, month, day] = datePart.split("-")
    const [hour, minute, second] = timePart.split(":")
    return type === "DATE" ? `${day}/${month}/${year}` : `${hour}:${minute}:${second.slice(0, 2)}`
  }

  const handleDownload = async (id, name) => {
    setLoading(true)
    try {
      const response = await downloadReport(id)
      const json = await response.json()
      // Navegación vía <a> en vez de window.open: la app corre embebida en un
      // iframe cross-origin y window.open tras un await pierde el gesto de
      // usuario, por lo que el navegador/sandbox del iframe lo bloquea en
      // silencio. El backend ya envía Content-Disposition: attachment, así
      // que el click dispara la descarga nativa sin abrir una pestaña nueva.
      const link = document.createElement("a")
      link.href = json.url
      link.download = name || ""
      link.rel = "noopener noreferrer"
      link.style.display = "none"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      alert("No se pudo descargar el archivo.")
    } finally {
      setLoading(false)
    }
  }

  const refreshAfterStateChange = () => {
    fetchReports(buildFilters(), pageIndex)
  }

  const handlePause = async (id) => {
    try {
      await pauseReport(id)
      setProgressData((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), status: "PAUSED" },
      }))
      showToast({ title: "Pausado", text: "Pausa solicitada. Surtirá efecto al terminar la página actual.", icon: "info" })
    } catch (e) {
      if (e.status === 409) {
        showToast({ title: "Aviso", text: "El reporte ya está terminando.", icon: "info" })
        refreshAfterStateChange()
      } else {
        showToast({ title: "Error", text: "No se pudo pausar el reporte.", icon: "error" })
      }
    }
  }

  const handleResume = async (id) => {
    try {
      await resumeReport(id)
      setProgressData((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), status: "RUNNING" },
      }))
      showToast({ title: "Reanudado", text: "El reporte se está procesando.", icon: "success" })
    } catch (e) {
      if (e.status === 409) {
        showToast({ title: "Aviso", text: "El reporte ya se está ejecutando.", icon: "info" })
        refreshAfterStateChange()
      } else {
        showToast({ title: "Error", text: "No se pudo reanudar el reporte.", icon: "error" })
      }
    }
  }

  const handleCancel = (id) => {
    Swal.fire({
      title: "¿Cancelar descarga?",
      text: "El reporte dejará de generarse y se descartarán los archivos parciales.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "Volver",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await cancelReport(id)
          setProgressData((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), status: "CANCELED" },
          }))
          showToast({ title: "Cancelando", text: "La cancelación surtirá efecto al terminar la página actual.", icon: "info" })
        } catch (e) {
          if (e.status === 409) {
            showToast({ title: "Aviso", text: "El reporte ya finalizó.", icon: "info" })
            refreshAfterStateChange()
          } else {
            showToast({ title: "Error", text: "No se pudo cancelar el reporte.", icon: "error" })
          }
        }
      }
    })
  }

  const handleDelete = (id) => {
    Swal.fire({
      title: "¿Eliminar reporte?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true)
        try {
          await deleteReport(id)
          showToast({ title: "Eliminado", text: "El reporte fue eliminado correctamente.", icon: "success" })
          fetchReports()
        } catch {
          showToast({ title: "Error", text: "No se pudo eliminar el reporte.", icon: "error" })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const fetchReports = useCallback(async (filters = {}, page = 0) => {
    try {
      const result = await fetchReportsService(filters, page)
      setTotalPages(result.totalPages)
      setData(result.content)
    } catch (error) {
      console.error("Error fetching reports:", error)
    }
  }, [])

  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  const fetchProgressOnce = useCallback(async () => {
    const reportsInProgress = reportsInProgressRef.current
    if (reportsInProgress.length === 0) return

    const progressPromises = reportsInProgress.map(async (report) => {
      try {
        const progress = await getReportProgress(report.id)
        return { ...progress, id: report.id }
      } catch (error) {
        console.error(`Error fetching progress for report ${report.id}:`, error)
        return null
      }
    })

    const results = await Promise.all(progressPromises)
    const newProgressData = {}
    const finishedIds = new Set()
    let shouldRefreshReports = false

    results.forEach((result) => {
      if (result) {
        const status = getReportStatus(null, result)
        const progressPct = result.progress ?? 0
        newProgressData[result.id] = result

        if (isTerminalReportStatus(status)) {
          finishedIds.add(result.id)
          shouldRefreshReports = true
          return
        }

        if (progressPct >= 100 && !completionRefreshRef.current[result.id]) {
          completionRefreshRef.current[result.id] = true
          shouldRefreshReports = true
        }
      }
    })

    if (Object.keys(newProgressData).length > 0) {
      setProgressData((prev) => ({
        ...prev,
        ...newProgressData,
      }))
    }

    if (finishedIds.size > 0) {
      reportsInProgressRef.current = reportsInProgress.filter(
        (report) => !finishedIds.has(report.id)
      )
    }

    if (shouldRefreshReports) {
      fetchReports(buildFilters(), pageIndex)
    }
  }, [
    fetchReports,
    extensionFilter,
    fileNameFilter,
    serviceFilter,
    userFilter,
    statusFilter,
    fechaDesde,
    fechaHasta,
    pageIndex,
  ])

  useEffect(() => {
    const visibleReportIds = new Set(data.map((d) => String(d.id)))
    reportsInProgressRef.current = data.filter(isActiveReport)
    completionRefreshRef.current = Object.fromEntries(
      Object.entries(completionRefreshRef.current).filter(([id]) => visibleReportIds.has(id))
    )
    setProgressData((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id]) => visibleReportIds.has(id))
      )
      const prevKeys = Object.keys(prev)
      const nextKeys = Object.keys(next)
      const hasSameKeys =
        prevKeys.length === nextKeys.length &&
        prevKeys.every((id) => Object.prototype.hasOwnProperty.call(next, id))
      return hasSameKeys ? prev : next
    })
    if (reportsInProgressRef.current.length > 0) {
      fetchProgressOnce()
    }
  }, [data, fetchProgressOnce])

  useEffect(() => {
    const anyNearCompletion = Object.values(progressData).some(
      (p) => getReportStatus(null, p) === "RUNNING" && (p?.progress ?? 0) >= 80
    )
    const intervalMs = anyNearCompletion ? FAST_POLLING_INTERVAL_MS : POLLING_INTERVAL_MS
    const interval = setInterval(fetchProgressOnce, intervalMs)
    return () => clearInterval(interval)
  }, [fetchProgressOnce, progressData])

  const handleSearch = () => {
    setPageIndex(0)
    fetchReports(buildFilters(), 0)
  }
  const handleClear = () => {
    setUserFilter("")
    setExtensionFilter("")
    setServiceFilter("")
    setFileNameFilter("")
    setStatusFilter("")
    setFechaDesde(undefined)
    setFechaHasta(undefined)
    setPageIndex(0)
    fetchReports({}, 0)
  }
  const gotoPage = (page) => {
    setPageIndex(page)
    fetchReports(buildFilters(), page)
  }

  const PaginationItems = () => {
    const pages = []

    let startPage = Math.max(pageIndex - 1, 0)
    let endPage = Math.min(pageIndex + 2, totalPages)

    for (let i = startPage; i < endPage; i++) {
      pages.push(i)
    }

    if (startPage > 0) {
      pages.unshift("...")
      pages.unshift(0)
    }

    if (endPage < totalPages) {
      pages.push("...")
      pages.push(totalPages - 1)
    }

    return (
      <>
        {pages.map((i, idx) =>
          i === "..." ? (
            <span key={idx + i} style={{ margin: "0 8px" }}>
              ...
            </span>
          ) : (
            <MDPagination item key={i} onClick={() => gotoPage(i)} active={pageIndex === i}>
              <MDTypography
                variant="caption"
                color={pageIndex === i ? "white" : darkMode ? "white" : "secondary"}
                sx={{ fontSize: 11 }}
              >
                {i + 1 >= 1000000
                  ? ((i + 1) / 1000000).toFixed(0) + "M"
                  : i + 1 >= 1000
                  ? ((i + 1) / 1000).toFixed(0) + "K"
                  : i + 1}
              </MDTypography>
            </MDPagination>
          )
        )}
      </>
    )
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
      <MDBox pt={5} pb={3}>
        <Grid container>
          <Grid item xs={12}>
            <Card sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="h6" color="text" gutterBottom>
                Filtros
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Extension</InputLabel>
                    <Select
                      label="Extension"
                      variant="outlined"
                      fullWidth
                      value={extensionFilter}
                      onChange={(e) => setExtensionFilter(e.target.value)}
                      sx={{ p: 1.5 }}
                      placeholder="Seleccione.."
                    >
                      <MenuItem key="1" value={"ZIP"}>
                        ZIP
                      </MenuItem>
                      <MenuItem key="0" value={"PDF"}>
                        PDF
                      </MenuItem>
                      <MenuItem key="0" value={"XLSX"}>
                        EXCEL
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Nombre de archivo"
                    variant="outlined"
                    fullWidth
                    value={fileNameFilter}
                    onChange={(e) => setFileNameFilter(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Servicio</InputLabel>
                    <Select
                      label="Servicio"
                      variant="outlined"
                      fullWidth
                      value={serviceFilter}
                      onChange={(e) => setServiceFilter(e.target.value)}
                      sx={{ p: 1.5 }}
                      placeholder="Seleccione.."
                    >
                      <MenuItem key="1" value={"INGRESOS"}>
                        INGRESOS
                      </MenuItem>
                      <MenuItem key="0" value={"RECONOCIMIENTOS"}>
                        RECONOCIMIENTOS
                      </MenuItem>
                      <MenuItem key="2" value={"LIVENESS"}>
                        LIVENESS
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Usuario"
                    variant="outlined"
                    fullWidth
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      label="Estado"
                      variant="outlined"
                      fullWidth
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      sx={{ p: 1.5 }}
                      placeholder="Seleccione.."
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="DONE">Correcto</MenuItem>
                      <MenuItem value="ERROR">Erróneo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Flatpickr
                    value={fechaDesde}
                    options={{
                      locale: "es",
                      enableTime: true,
                      time_24hr: true,
                      defaultHour: new Date().getHours(),
                      defaultMinute: new Date().getMinutes(),
                    }}
                    onChange={(date) => setFechaDesde(date[0])}
                    render={({ defaultValue, value, ...props }, ref) => (
                      <TextField
                        {...props}
                        fullWidth
                        label="Fecha Desde"
                        variant="outlined"
                        value={formatDateForApi(fechaDesde)}
                        inputRef={ref}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Flatpickr
                    value={fechaHasta}
                    options={{
                      locale: "es",
                      enableTime: true,
                      time_24hr: true,
                      defaultHour: new Date().getHours(),
                      defaultMinute: new Date().getMinutes(),
                    }}
                    onChange={(date) => setFechaHasta(date[0])}
                    render={({ defaultValue, value, ...props }, ref) => (
                      <TextField
                        {...props}
                        fullWidth
                        label="Fecha Hasta"
                        variant="outlined"
                        value={formatDateForApi(fechaHasta)}
                        inputRef={ref}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <MDBox display="flex" justifyContent="flex-start" alignItems="center" mt={2}>
                    <MDButton variant="gradient" color="info" onClick={handleSearch}>
                      Buscar
                    </MDButton>
                    &nbsp;
                    <MDButton variant="outlined" color="info" onClick={handleClear}>
                      Limpiar
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <MDBox>
              <Card sx={{ p: 2 }}>
                <DataTable
                  isSorted={false}
                  noEndBorder
                  showTotalEntries={false}
                  entriesPerPage={false}
                  canSearch={false}
                  table={{
                    columns: columns,
                    rows: data,
                  }}
                />
                {totalPages > 1 && (
                  <MDBox display="flex" justifyContent="center" mt={3} mb={2}>
                    <MDPagination variant="gradient" color="info" size="small">
                      <MDPagination
                        item
                        onClick={() => gotoPage(pageIndex - 1)}
                        disabled={pageIndex === 0}
                      >
                        <Icon>keyboard_arrow_left</Icon>
                      </MDPagination>
                      <PaginationItems />
                      <MDPagination
                        item
                        onClick={() => gotoPage(pageIndex + 1)}
                        disabled={pageIndex >= totalPages - 1}
                      >
                        <Icon>keyboard_arrow_right</Icon>
                      </MDPagination>
                    </MDPagination>
                  </MDBox>
                )}
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default ReportCenterPage
